import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '@guildeducationinc/guild-auth';
import { datadogRum } from '@datadog/browser-rum';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import { ACADEMIC_SSO_LINK_QUERY } from '@src/graphql/home/queries';
import { ECORNELL_ACADEMIC_PARTNER_ID } from '@src/constants';

export interface ECornellSSOLinkData {
  guildProgramName: string;
  ssoLink?: string;
  expired: boolean;
}

export interface ECornellSSOResponse {
  ssoLinkData: ECornellSSOLinkData;
}

interface ECornellSSOVariables {
  guildUuid: string;
  academicPartnerId: string;
}

const useECornellSSOLinks = () => {
  const { user } = useAuth();
  const { loading, data, error } = useQuery<
    ECornellSSOResponse & { Error: string },
    ECornellSSOVariables
  >(ACADEMIC_SSO_LINK_QUERY, {
    context: {
      server: Server.GLOBAL_APP_SYNC,
    },
    variables: {
      guildUuid: user?.id ?? '',
      academicPartnerId: ECORNELL_ACADEMIC_PARTNER_ID,
    },
    skip: !user,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (!loading && error) {
      datadogRum.addError(error, {
        message: 'BLOCKING: There was an error loading user eCornell SSO link',
      });
      error.clientErrors.forEach((e) => {
        datadogRum.addError(e, { message: 'Apollo Client Error' });
      });
      error.graphQLErrors.forEach((e) => {
        datadogRum.addError(e, { message: 'Apollo GraphQL Error' });
      });
      if (error.networkError) {
        datadogRum.addError(error.networkError, { message: 'Apollo Network Error' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error]);

  return {
    loading,
    // data exists and is not expired
    data: data?.ssoLinkData && !data?.ssoLinkData.expired ? data?.ssoLinkData : undefined,
    error,
  };
};

export default useECornellSSOLinks;
